import { Button, Card, CardContent, Divider, Grid, MenuItem, Chip as MuiChip, TablePagination, TextField } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { ListFilterGroup } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { useLoadingState, useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { VehicleToDoListParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'

export default function WaitingOnThirdPartyAdditionalProgrammingToDoListDetails() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [loadingState, setLoadingState] = useLoadingState({ isGettingVehicles: false })
  const [parameters, setParameters] = useState<VehicleToDoListParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDate,exporterBuyer',
    order: 'asc'
  })
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>()

  const getWaitingOnThirdPartyAdditionalProgrammingToDoList = useDebounce((parameters: VehicleToDoListParameters) => {
    setLoadingState('isGettingVehicles', true)

    controllers.toDoLists
      .getWaitingOnThirdPartyAdditionalProgrammingToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Waiting On 3rd Party Additional Programming')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingVehicles)
  }, [layout, loadingState.isGettingVehicles])

  useEffect(() => {
    getWaitingOnThirdPartyAdditionalProgrammingToDoList(parameters)
  }, [getWaitingOnThirdPartyAdditionalProgrammingToDoList, parameters])

  const importBranchGuid = query.get('importBranchGuid') ?? undefined
  useEffect(() => {
    setParameters({ ...parameters, importBranchGuid })
  }, [importBranchGuid])

  const isDateExceedingLimit = (date: string | Date, limit: number) => {
    if (!date || isNaN(new Date(date).getTime())) return false
    return moment().diff(moment(date), 'days') >= limit
  }

  const formatDateToMMDDYYYY = (date: string | Date | undefined): string => {
    if (!date || isNaN(new Date(date).getTime())) return ''
    return formatDate(date)
  }

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm='auto' alignContent='center'>
              <ListFilterGroup>
                <TextField
                  fullWidth
                  label='Filter Field'
                  margin='dense'
                  onChange={e => {
                    setFilterField(e.target.value)
                    setFilterValue('')
                  }}
                  select
                  size='small'
                  sx={{ minWidth: 200 }}
                  value={filterField || ''}
                >
                  <MenuItem value='clusterConversionCompletedDate'>Cluster Conversion Completed Date</MenuItem>

                  <MenuItem value='clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDate'>
                    Cluster Delivered To Third Party Additional Programming Company Date
                  </MenuItem>

                  <MenuItem value='exporterBuyer'>Exporter/Buyer</MenuItem>

                  <MenuItem value='vin'>VIN</MenuItem>

                  <MenuItem value='year'>Year</MenuItem>

                  <MenuItem value='make'>Make</MenuItem>

                  <MenuItem value='model'>Model</MenuItem>

                  <MenuItem value='importerStockNumber'>Importer Stock#</MenuItem>
                </TextField>

                {['', 'exporterBuyer', 'vin', 'year', 'make', 'model', 'importerStockNumber'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      margin='dense'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      size='small'
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {filterField === 'clusterConversionCompletedDate' && (
                  <>
                    <Grid container>
                      <Grid item xs={12} sm='auto'>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                          label='Filter Date From'
                          onChange={e => {
                            setFilterValueFrom(e.target.value)
                          }}
                          type='date'
                          value={filterValueFrom}
                        />
                      </Grid>

                      <Grid item xs={12} sm='auto'>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                          label='Filter Date To'
                          onChange={e => {
                            setFilterValueTo(e.target.value)
                          }}
                          type='date'
                          value={filterValueTo}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {filterField === 'clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDate' && (
                  <>
                    <Grid container>
                      <Grid item xs={12} sm='auto'>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                          label='Filter Date From'
                          onChange={e => {
                            setFilterValueFrom(e.target.value)
                          }}
                          type='date'
                          value={filterValueFrom}
                        />
                      </Grid>

                      <Grid item xs={12} sm='auto'>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                          label='Filter Date To'
                          onChange={e => {
                            setFilterValueTo(e.target.value)
                          }}
                          type='date'
                          value={filterValueTo}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField ||
                      (filterField === 'clusterConversionCompletedDate' ? !filterValueFrom || !filterValueTo : !filterValue) ||
                      (filterField === 'clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDate'
                        ? !filterValueFrom || !filterValueTo
                        : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'clusterConversionCompletedDate') {
                        setParameters({
                          ...parameters,
                          clusterConversionCompletedDateFrom: filterValueFrom,
                          clusterConversionCompletedDateTo: filterValueTo
                        })
                      } else if (filterField === 'clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDate') {
                        setParameters({
                          ...parameters,
                          clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateFrom: filterValueFrom,
                          clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateTo: filterValueTo
                        })
                      } else if (filterField === 'exporterBuyer') {
                        setParameters({
                          ...parameters,
                          exporter: filterValue,
                          buyer: filterValue
                        })
                      } else {
                        setParameters({
                          ...parameters,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        parameters.clusterConversionCompletedDateTo ||
                          parameters.clusterConversionCompletedDateFrom ||
                          parameters.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateTo ||
                          parameters.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateFrom ||
                          parameters.exporter ||
                          parameters.buyer ||
                          parameters.vin ||
                          parameters.year ||
                          parameters.make ||
                          parameters.model ||
                          parameters.importerStockNumber
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setParameters({
                        ...parameters,
                        clusterConversionCompletedDateTo: 'undefined',
                        clusterConversionCompletedDateFrom: 'undefined',
                        clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateTo: 'undefined',
                        clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateFrom: 'undefined',
                        exporter: 'undefined',
                        buyer: 'undefined',
                        vin: 'undefined',
                        year: undefined,
                        make: 'undefined',
                        model: 'undefined',
                        importerStockNumber: 'undefined'
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </ListFilterGroup>
            </Grid>
          </Grid>

          <Grid item xs={12} container spacing={1} alignItems='center'>
            {parameters.clusterConversionCompletedDateFrom && parameters.clusterConversionCompletedDateTo && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Cluster Conversion Completed Date from "${moment(
                    parameters.clusterConversionCompletedDateFrom
                  ).format('M/D/yyyy')}" to "${moment(parameters.clusterConversionCompletedDateTo).format('M/D/yyyy')}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      clusterConversionCompletedDateFrom: undefined,
                      clusterConversionCompletedDateTo: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateFrom &&
              parameters.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Cluster Delivered To Third Party Additional Programming Company Date from "${moment(
                      parameters.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateFrom
                    ).format('M/D/yyyy')}" to "${moment(
                      parameters.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateFrom: undefined,
                        clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
            {(parameters.exporter || parameters.buyer) && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Exporter/Buyer Contains "${parameters.exporter || parameters.buyer}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      exporter: undefined,
                      buyer: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.vin && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`VIN Contains "${parameters.vin}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      vin: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.year && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Year Contains "${parameters.year}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      year: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.make && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Make Contains "${parameters.make}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      make: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.model && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Model Contains "${parameters.model}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      model: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.importerStockNumber && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Importer Stock# Contains "${parameters.importerStockNumber}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      importerStockNumber: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {vehicles?.map(vehicle => (
        <Card sx={{ mb: 2, mt: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12}>
                <Grid container spacing={1} alignItems='flex-start'>
                  <Grid item xs={12}>
                    <LabelField
                      label='Cluster Conversion Completed:'
                      value={formatDateToMMDDYYYY(vehicle.clusterConversionCompletedDate)}
                      error={
                        !!vehicle.clusterConversionCompletedDate &&
                        isDateExceedingLimit(vehicle.clusterConversionCompletedDate, 4)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField
                      label='Cluster Delivered To 3rd Party Additional Programming Company:'
                      value={formatDateToMMDDYYYY(vehicle?.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDate)}
                      error={
                        !!vehicle?.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDate &&
                        isDateExceedingLimit(vehicle?.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDate, 2)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField
                      label='Exporter/Buyer'
                      value={vehicle?.buyerCompany?.name ? vehicle.buyerCompany.name : vehicle?.exporterCompany?.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='VIN' value={vehicle.vin} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='Year' value={vehicle.year} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='Make' value={vehicle.make} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='Model' value={vehicle.model} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='Importer Stock#' value={vehicle.importerStockNumber} />
                  </Grid>

                  <Grid item xs={12} container justifyContent='flex-end' alignItems='center'>
                    <Button
                      onClick={() => {
                        navigate('/vehicles/' + vehicle.vehicleGuid + '/waiting-on-third-party-additional-programming')
                      }}
                      variant='contained'
                    >
                      SCAN CLUSTER
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Grid item xs={12} marginTop={1}>
        <ExplanationAccordion>
          || Vehicles appear in this to do list if Vehicle Check-In = Complete, Convert Cluster = Yes, Additional Programming =
          Yes, Additional Programming Company = 3rd Party, Pulled Cluster Photo Uploaded = Yes, “Cluster Conversion Completed” =
          Populated, "Cluster Delivered to 3rd Party Additional Programming Company" = Populated, and "Additional Programming
          Complete" = Not populated. || The “Waiting on 3rd Party Additional Programming” To Do List card’s “Cluster Conversion
          Completed” value will be in bold and red if current date is ≥ Cluster Conversion Completed + 4 days. || The “Waiting
          on 3rd Party Additional Programming” To Do List card’s “Cluster Delivered to 3rd Party Additional Programming Company”
          value will be in bold and red if current date is ≥ “Cluster Delivered to 3rd Party Additional Programming Company” + 2
          days. || The “Waiting on 3rd Party Additional Programming” To Do List card’s “Exporter/Buyer” field displays the name
          of the Buyer company associated with the vehicle if one exists, else the name of the Exporter company. || The “Waiting
          on 3rd Party Additional Programming” To Do List cards are sorted by “Cluster Delivered to 3rd Party Additional
          Programming Company” oldest to newest and then by Exporter/Buyer alphabetically. || The Waiting on 3rd Party
          Additional Programming “To Do List” is a card screen intended for users on a phone.||
        </ExplanationAccordion>
      </Grid>

      <Grid item xs={12}>
        <TablePagination
          component='div'
          count={totalCount}
          onPageChange={(_, page: number) => {
            setParameters({
              ...parameters,
              page
            })
          }}
          onRowsPerPageChange={e => {
            setParameters({
              ...parameters,
              page: 0,
              pageSize: e.target.value as unknown as number
            })
          }}
          page={parameters.page}
          rowsPerPage={parameters.pageSize}
          labelRowsPerPage={
            <Grid container spacing={1}>
              <Grid item>Order By:</Grid>

              <Grid item>
                <TextField
                  InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                  onChange={e => {
                    setParameters({ ...parameters, orderBy: e.target.value })
                  }}
                  select
                  size='small'
                  value={parameters.orderBy}
                  variant='standard'
                >
                  <MenuItem value='clusterConversionCompletedDate'> Cluster Conversion Completed Date</MenuItem>

                  <MenuItem value='clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDate'>
                    Cluster Delivered To Third Party Additional Programming Company Date
                  </MenuItem>

                  <MenuItem value='exporterBuyer'>Exporter/Buyer</MenuItem>

                  <MenuItem value='vin'>VIN</MenuItem>

                  <MenuItem value='year'>Year</MenuItem>

                  <MenuItem value='make'>Make</MenuItem>

                  <MenuItem value='model'>Model</MenuItem>

                  <MenuItem value='importerStockNumber'>Importer Stock#</MenuItem>
                </TextField>
              </Grid>

              <Grid item>
                <TextField
                  InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                  onChange={e => {
                    setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
                  }}
                  select
                  size='small'
                  value={parameters.order}
                  variant='standard'
                >
                  <MenuItem value='asc'>Ascending</MenuItem>

                  <MenuItem value='desc'>Descending</MenuItem>
                </TextField>
              </Grid>

              <Grid item sx={{ alignSelf: 'stretch' }}>
                <Divider orientation='vertical' />
              </Grid>

              <Grid item>Cards Per Page:</Grid>
            </Grid>
          }
          rowsPerPageOptions={[
            { label: '5', value: 5 },
            { label: '10', value: 10 },
            { label: '25', value: 25 },
            { label: '50', value: 50 },
            { label: '100', value: 100 }
          ]}
          SelectProps={{ fullWidth: false }}
        />
      </Grid>
    </>
  )
}
